// Lightweight client-side rewards / discount engine.
// Stores progress in localStorage so users get rewarded for engagement.

export type DiscountCode = {
  code: string;
  label: string;
  percent: number; // discount percent
  reason: string;
  proofRequired?: boolean;
  proofType?: "senior" | "veteran";
};

export const DISCOUNTS: Record<string, DiscountCode> = {
  WELCOME5: { code: "WELCOME5", label: "Welcome", percent: 5, reason: "Thanks for visiting Metroshine" },
  EXPLORER10: { code: "EXPLORER10", label: "Explorer", percent: 10, reason: "Browsed 5+ pages" },
  ESTIMATE10: { code: "ESTIMATE10", label: "Estimate Bonus", percent: 10, reason: "Built an instant estimate" },
  RETURN15: { code: "RETURN15", label: "Welcome Back", percent: 15, reason: "Returning visitor" },
  REFERRAL20: { code: "REFER20", label: "Referral", percent: 20, reason: "Referred by a friend" },
  SENIOR10: {
    code: "SENIOR10",
    label: "Senior Discount",
    percent: 10,
    reason: "Honoring our seniors (65+)",
    proofRequired: true,
    proofType: "senior",
  },
  VETERAN15: {
    code: "VETERAN15",
    label: "Veteran Discount",
    percent: 15,
    reason: "Thank you for your service",
    proofRequired: true,
    proofType: "veteran",
  },
};

const STORAGE_KEY = "metroshine_rewards_v1";

export type RewardsState = {
  pageViews: number;
  visitedRoutes: string[];
  unlocked: string[]; // codes
  firstVisitAt: number;
  lastVisitAt: number;
  visits: number;
  estimateCompleted: boolean;
  shownPopups: string[]; // popup ids already shown this session-ish
};

const defaultState = (): RewardsState => ({
  pageViews: 0,
  visitedRoutes: [],
  unlocked: [],
  firstVisitAt: Date.now(),
  lastVisitAt: Date.now(),
  visits: 1,
  estimateCompleted: false,
  shownPopups: [],
});

export const getRewards = (): RewardsState => {
  if (typeof window === "undefined") return defaultState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    return { ...defaultState(), ...JSON.parse(raw) };
  } catch {
    return defaultState();
  }
};

export const saveRewards = (s: RewardsState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* noop */
  }
};

export const unlockCode = (code: string) => {
  const s = getRewards();
  if (!s.unlocked.includes(code)) {
    s.unlocked.push(code);
    saveRewards(s);
  }
};

export const markPopupShown = (id: string) => {
  const s = getRewards();
  if (!s.shownPopups.includes(id)) {
    s.shownPopups.push(id);
    saveRewards(s);
  }
};

export const hasShownPopup = (id: string) => getRewards().shownPopups.includes(id);

export const trackPageView = (path: string) => {
  const s = getRewards();
  s.pageViews += 1;
  s.lastVisitAt = Date.now();
  if (!s.visitedRoutes.includes(path)) s.visitedRoutes.push(path);
  saveRewards(s);
  return s;
};

export const markEstimateCompleted = () => {
  const s = getRewards();
  s.estimateCompleted = true;
  saveRewards(s);
};

export const isReturningVisitor = () => {
  const s = getRewards();
  // Returning if first visit was more than 1 day ago
  return Date.now() - s.firstVisitAt > 24 * 60 * 60 * 1000;
};

export const incrementVisitOnce = () => {
  const s = getRewards();
  const key = "metroshine_session_visit";
  if (!sessionStorage.getItem(key)) {
    s.visits += 1;
    sessionStorage.setItem(key, "1");
    saveRewards(s);
  }
};
